/**
 * Token Service
 * AIDLC Administrative Backend
 *
 * API calls for token usage tracking, budget management,
 * and cost analytics across all agents and projects.
 */

import apiClient from './api';

// ── Types ──────────────────────────────────────────────────────────

export interface TokenSummary {
  totalSpend: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  avgCostPerSession: number;
  projectedMonthlySpend: number;
  budgetUtilization: number;
  monthlyBudget: number;
  recordCount: number;
  /** The window every figure above is counted over. */
  windowDays: number;
  /** Unwindowed, so an empty month cannot read as an empty history. */
  lifetimeSpend: number;
  lifetimeInputTokens: number;
  lifetimeOutputTokens: number;
  lifetimeRecordCount: number;
  /** When the last agent session was recorded, or null if none ever was. */
  lastRecordedAt: string | null;
}

export interface AgentTokenUsage {
  agentId: string;
  agentName: string;
  role: string;
  avatarColor: string;
  model: string;
  tasks: number;
  inputTokens: number;
  outputTokens: number;
  totalCost: number;
  lastActive: string;
  status: string;
}

export interface ProjectTokenUsage {
  project: string;
  spend: number;
  tokens: number;
  sessions: number;
}

export interface TrendDataPoint {
  date: string;
  spend: number;
  budgetThreshold: number;
}

export interface SessionRecord {
  id: string;
  timestamp: string;
  agentName: string;
  agentId: string;
  project: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  duration: number;
  taskDescription: string;
}

export interface VerificationStatus {
  configured: boolean;
}

export interface ModelComparison {
  model: string;
  localInputTokens: number;
  localOutputTokens: number;
  localCost: number;
  anthropicInputTokens: number;
  anthropicOutputTokens: number;
  anthropicCost: number;
  tokenDelta: number;
  costDelta: number;
  variancePercent: number;
  status: 'green' | 'amber' | 'red';
}

export interface ReconciliationReport {
  startDate: string;
  endDate: string;
  generatedAt: string;
  localTotalCost: number;
  anthropicTotalCost: number;
  totalCostDelta: number;
  totalVariancePercent: number;
  models: ModelComparison[];
}

export interface BudgetConfig {
  monthlyLimit: number;
  warningThreshold: number;
  criticalThreshold: number;
  pricing: {
    opus: { input: number; output: number };
    sonnet: { input: number; output: number };
    haiku: { input: number; output: number };
  };
}

// ── API Calls ──────────────────────────────────────────────────────

const TOKEN_PATH = '/tokens';

/**
 * Get executive summary of token usage for the current month
 */
export async function getTokenSummary(): Promise<TokenSummary> {
  const res = await apiClient.get<TokenSummary>(TOKEN_PATH);
  return res.data;
}

/**
 * Get token usage broken down by agent
 */
export async function getTokensByAgent(): Promise<AgentTokenUsage[]> {
  const res = await apiClient.get<AgentTokenUsage[]>(`${TOKEN_PATH}/agents`);
  return res.data;
}

/**
 * Get token usage broken down by project
 */
export async function getTokensByProject(): Promise<ProjectTokenUsage[]> {
  const res = await apiClient.get<ProjectTokenUsage[]>(`${TOKEN_PATH}/projects`);
  return res.data;
}

/**
 * Get daily spend trend data
 */
export async function getTokenTrend(days = 30): Promise<TrendDataPoint[]> {
  const res = await apiClient.get<TrendDataPoint[]>(`${TOKEN_PATH}/trend?days=${days}`);
  return res.data;
}

/**
 * Get detailed session records with optional filters
 */
export async function getTokenSessions(params?: {
  agent?: string;
  project?: string;
  days?: number;
  limit?: number;
}): Promise<SessionRecord[]> {
  const res = await apiClient.get<SessionRecord[]>(`${TOKEN_PATH}/sessions`, { params });
  return res.data;
}

/**
 * Get current budget configuration
 */
export async function getBudgetConfig(): Promise<BudgetConfig> {
  const res = await apiClient.get<BudgetConfig>(`${TOKEN_PATH}/budget`);
  return res.data;
}

/**
 * Update budget configuration (partial updates allowed)
 */
export async function updateBudgetConfig(config: Partial<BudgetConfig>): Promise<BudgetConfig> {
  const res = await apiClient.put<BudgetConfig>(`${TOKEN_PATH}/budget`, config);
  return res.data;
}

// ── Billing Verification ────────────────────────────────────────────

/**
 * Check if the Anthropic Admin API is configured on the backend
 */
export async function getVerificationStatus(): Promise<VerificationStatus> {
  const res = await apiClient.get<VerificationStatus>(`${TOKEN_PATH}/verify/status`);
  return res.data;
}

/**
 * Get raw Anthropic usage data for a date range
 */
export async function getAnthropicUsage(startDate: string, endDate: string): Promise<unknown[]> {
  const res = await apiClient.get(`${TOKEN_PATH}/verify/usage`, {
    params: { startDate, endDate },
  });
  return res.data;
}

/**
 * Run a full reconciliation comparing local vs Anthropic data
 */
export async function reconcileUsage(startDate: string, endDate: string): Promise<ReconciliationReport> {
  const res = await apiClient.get<ReconciliationReport>(`${TOKEN_PATH}/reconcile`, {
    params: { startDate, endDate },
  });
  return res.data;
}
